import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import * as db from '@/lib/data-service';
import { Button } from '@/components/ui/button';
import { ChevronLeft, LogOut, Trash2, Moon, Sun, Bell, Shield, Heart, ExternalLink, PauseCircle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

export default function Settings() {
  const { logout, user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [profilePaused, setProfilePaused] = useState(!!profile?.profile_paused);

  useEffect(() => {
    setDarkMode(document.documentElement.classList.contains('dark'));
  }, []);

  const toggleDark = () => {
    document.documentElement.classList.toggle('dark');
    const isDark = document.documentElement.classList.contains('dark');
    setDarkMode(isDark);
    localStorage.setItem('jiv_dark_mode', isDark);
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    const result = await db.deleteAccount(user?.id);
    if (!result?.success) {
      const reason = result?.failedSteps?.[0] || 'Unknown error';
      toast(`Could not delete account fully: ${reason}`);
      setDeleting(false);
      return;
    }
    logout();
    navigate('/');
    toast('Account deleted');
    setDeleting(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const togglePause = () => {
    const next = !profilePaused;
    setProfilePaused(next);
    db.updateProfile(user.id, { profile_paused: next });
    toast(next ? 'Profile paused · you won\'t appear in discovery' : 'Profile active · you\'re back in discovery');
    if (refreshProfile) refreshProfile();
  };

  return (
    <div className="h-full overflow-auto bg-background max-w-md mx-auto" data-testid="settings-screen">
      <div className="flex items-center gap-3 px-4 py-4 border-b border-border">
        <button onClick={() => navigate('/profile')} data-testid="settings-back"><ChevronLeft className="h-5 w-5 text-foreground" /></button>
        <h1 className="text-xl font-bold text-foreground">Settings</h1>
      </div>

      <div className="p-4 space-y-1">
        {/* Dark mode */}
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center gap-3">
            {darkMode ? <Moon className="h-5 w-5 text-foreground" /> : <Sun className="h-5 w-5 text-foreground" />}
            <span className="text-sm text-foreground">Dark mode</span>
          </div>
          <Switch checked={darkMode} onCheckedChange={toggleDark} data-testid="dark-mode-toggle" />
        </div>

        <div className="border-b border-border" />

        {/* Notifications */}
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center gap-3">
            <Bell className="h-5 w-5 text-foreground" />
            <span className="text-sm text-foreground">Notifications</span>
          </div>
          <Switch defaultChecked data-testid="notifications-toggle" />
        </div>

        <div className="border-b border-border" />

        {/* Pause Profile */}
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center gap-3">
            <PauseCircle className="h-5 w-5 text-foreground" />
            <div>
              <span className="text-sm text-foreground block">Pause Profile</span>
              <span className="text-xs text-muted-foreground">Take a break from discovery</span>
            </div>
          </div>
          <Switch checked={profilePaused} onCheckedChange={togglePause} data-testid="pause-profile-toggle" />
        </div>

        <div className="border-b border-border" />

        {/* Block list */}
        <button className="flex items-center gap-3 py-3 w-full text-left" data-testid="block-list-btn">
          <Shield className="h-5 w-5 text-foreground" />
          <span className="text-sm text-foreground">Block list</span>
        </button>

        <div className="border-b border-border" />

        {/* Support */}
        <a href="mailto:hello@jivapp.in" className="flex items-center gap-3 py-3 w-full text-left">
          <Heart className="h-5 w-5 text-foreground" />
          <span className="text-sm text-foreground">Support: hello@jivapp.in</span>
        </a>

        <div className="border-b border-border" />

        {/* Donation */}
        <div className="p-4 rounded-xl border border-border bg-card mt-4">
          <p className="text-sm text-foreground leading-relaxed">
            Jiv is free and community-sustained. If it worked for you, consider supporting it.
          </p>
          <a href="https://razorpay.me" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-primary font-medium mt-2" data-testid="donate-link">
            Support Jiv <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>

        <div className="mt-6 space-y-3">
          <Button variant="outline" className="w-full rounded-xl" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="h-4 w-4 mr-2" />Logout
          </Button>
          <Button variant="destructive" className="w-full rounded-xl" onClick={() => setShowDeleteModal(true)} data-testid="delete-account-btn">
            <Trash2 className="h-4 w-4 mr-2" />Delete account
          </Button>
        </div>
      </div>

      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-sm rounded-xl">
          <DialogHeader>
            <DialogTitle>Delete account</DialogTitle>
            <DialogDescription>This will permanently delete your profile and all data. This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} className="rounded-xl">Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} className="rounded-xl" data-testid="confirm-delete-btn">{deleting ? 'Deleting...' : 'Delete'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
